// src/App.js
import "./App.css";
import Header from "./Component/Header";
import BodyElement from "./Component/BodyElement";
import { useState, useContext, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MyContext } from "./Context";
import MsgFinal from "./Component/MsgFinal";
import authentication from "./utils/authentication";
import { api } from "./service/Api";
import Logos from "./utils/logoHospital";
import { ErrorBox } from "./Component/error";

/* --- util: base64/base64url decode seguro --- */
function safeB64Decode(input) {
  if (!input) return null;
  try {
    let s = String(input).replace(/-/g, "+").replace(/_/g, "/");
    const pad = s.length % 4;
    if (pad) s += "=".repeat(4 - pad);

    if (typeof window !== "undefined" && typeof window.atob === "function") {
      return decodeURIComponent(
        Array.prototype.map
          .call(window.atob(s), function (c) {
            return "%" + c.charCodeAt(0).toString(16).padStart(2, "0");
          })
          .join("")
      );
    }
    // eslint-disable-next-line no-undef
    return Buffer.from(s, "base64").toString("utf-8");
  } catch {
    return null;
  }
}

const SETORES = [
  { tipo: "G", label: "Geral" },
  { tipo: "PI", label: "Internação" },
  { tipo: "PE", label: "Pronto Socorro" },
  { tipo: "ES", label: "Ambulatório de Especialidades" },
  { tipo: "UI", label: "Unidade de Imagens" },
];

function App() {
  const navigate = useNavigate();
  const { question, setQuestion } = useContext(MyContext);

  const [perguntas, setPerguntas] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const { tipo, cnes } = useParams();

  const cnesDecoded = useMemo(() => safeB64Decode(cnes) || "", [cnes]);

  const tipoNorm = useMemo(() => {
    const t = String(tipo || "").toUpperCase();
    const allowed = ["G", "PE", "PI", "ES", "UI"];
    return allowed.includes(t) ? t : "";
  }, [tipo]);

  const authToken = useMemo(
    () => (cnesDecoded ? authentication[cnesDecoded] : undefined),
    [cnesDecoded]
  );

  const logoSrc = useMemo(
    () => (cnesDecoded && Logos[cnesDecoded] ? Logos[cnesDecoded] : undefined),
    [cnesDecoded]
  );

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  /* ---------------- CONCORRÊNCIA / CACHE ---------------- */
  const abortRef = useRef(null);       // AbortController da request atual
  const inFlightRef = useRef(false);   // evita chamadas paralelas
  const lastQueryKeyRef = useRef("");  // chave da última consulta iniciada
  const cacheRef = useRef(new Map());  // cache por (cnesDecoded|tipoNorm)

  const queryKey = useMemo(
    () => (cnesDecoded && tipoNorm ? `${cnesDecoded}|${tipoNorm}` : ""),
    [cnesDecoded, tipoNorm]
  );

  async function listPerguntasOnce() {
    // parâmetros faltando: limpa e sai
    if (!cnesDecoded || !tipoNorm) {
      setPerguntas([]);
      return;
    }

    // cache hit
    if (cacheRef.current.has(queryKey)) {
      setPerguntas(cacheRef.current.get(queryKey));
      return;
    }

    // cancela request anterior, se houver
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { }
    }

    // prepara nova consulta
    lastQueryKeyRef.current = queryKey;
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      if (inFlightRef.current) return; // outra chamada já em andamento
      inFlightRef.current = true;

      setLoading(true);
      setError(false);

      const config = {
        signal: controller.signal, // axios moderno suporta AbortController
        ...(authToken ? { headers: { "x-access-token": authToken } } : {}),
      };

      const { data } = await api.get(
        `/satisfacao/list_perguntas/${encodeURIComponent(cnesDecoded)}/${encodeURIComponent(
          tipoNorm
        )}`,
        config
      );

      // aplica resposta apenas se ainda é a consulta atual
      if (lastQueryKeyRef.current !== queryKey) return;

      if (Array.isArray(data) && data.length > 0) {
        cacheRef.current.set(queryKey, data);
        setPerguntas(data);
      } else {
        cacheRef.current.set(queryKey, []);
        setPerguntas([]);
        openModal();
      }
    } catch (e) {
      // ignora erros de cancelamento
      if (e?.name !== "CanceledError" && e?.name !== "AbortError") {
        console.error(e);
        setError(true);
        setPerguntas([]);
      }
    } finally {
      if (lastQueryKeyRef.current === queryKey) {
        setLoading(false);
      }
      inFlightRef.current = false;
    }
  }

  // navega para a rota principal usando o mesmo :cnes (codificado) + próximo tipo
  function gotoTipo(nextTipo) {
    navigate(`/${cnes}/${nextTipo}`);
    localStorage.setItem("tipo", nextTipo);
  }

  useEffect(() => {
    setQuestion(0);          // opcional: reset ao trocar setor/URL
    listPerguntasOnce();
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch { }
      }
      inFlightRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  return (
    <>
      <Header />

      {/* Estado sem 'tipo' selecionado */}
      {!tipoNorm && (
        <div className="ps-screen">
          <div className="ps-card">
            <p>Selecione um setor:</p>
            <div className="ps-sector-grid">
              {SETORES.map((s) => (
                <button
                  key={s.tipo}
                  type="button"
                  className="ps-sector-btn"
                  onClick={() => gotoTipo(s.tipo)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {tipoNorm && loading && (
        <div className="ps-screen">
          <div className="ps-card">
            <p className="ps-loading-text">Carregando perguntas...</p>
          </div>
        </div>
      )}

      {/* Perguntas quando há dados */}
      {tipoNorm &&
        !loading &&
        perguntas.length > 0 &&
        perguntas.map((item, index) => {
          if (question !== index) return null;
          return (
            <div key={item?.ID_PERGUNTA || index} className="ps-screen">
              <BodyElement
                pergunta={item?.PERGUNTA}
                tipo={tipoNorm}
                id_pergunta={item?.ID_PERGUNTA}
              />
              {logoSrc && (
                <div className="ps-logo-wrap">
                  <img src={logoSrc} alt="Logo do Hospital" className="ps-logo" />
                </div>
              )}
            </div>
          );
        })}

      {/* Sem perguntas cadastradas */}
      {tipoNorm && !loading && perguntas.length === 0 && !error && (
        <div className="ps-screen">
          <div className="ps-card">
            <p>Sem perguntas cadastradas para esse setor!</p>
          </div>
          {logoSrc && (
            <div className="ps-logo-wrap">
              <img src={logoSrc} alt="Logo do Hospital" className="ps-logo" />
            </div>
          )}
        </div>
      )}

      {/* Erro de carregamento */}
      {error && <ErrorBox />}

      {/* Modal "Atenção! Nenhuma pergunta" */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h2>Atenção!</h2>
            <p>Nenhuma pergunta cadastrada!</p>
            <button className="btn-ok" onClick={closeModal}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Mensagem final */}
      {perguntas.length > 0 && question >= perguntas.length && (
        <div>
          <MsgFinal />
        </div>
      )}
    </>
  );
}

export default App;